import React, { useMemo } from 'react';
import {
  IonBackButton,
  IonButtons,
  IonButton,
  IonContent,
  IonHeader,
  IonItem,
  IonLabel,
  IonList,
  IonPage,
  IonSpinner,
  IonText,
  IonTitle,
  IonToolbar,
} from '@ionic/react';
import { useParams, useHistory } from 'react-router-dom';
import { useRecipes } from '../hooks/useRecipes';
import { useLanguage } from '../context/LanguageContext';
import LanguageToggle from '../components/LanguageToggle';
import UserMenu from '../components/UserMenu';
import { formatCurrency, formatDecimal } from '../utils/format';
import { IProcessedMaterial, IRawMaterial } from 'shared';

interface Params {
  id: string;
}

const RecipeDetail: React.FC = () => {
  const { id } = useParams<Params>();
  const history = useHistory();
  const { t, language } = useLanguage();
  const locale = language === 'vi' ? 'vi-VN' : 'en-US';
  const {
    recipes,
    menuItems,
    processedMaterials,
    rawMaterials,
    isLoading,
  } = useRecipes();

  const menuItem = menuItems.find((item) => item.id === id) || null;

  const recipeEntries = useMemo(() => {
    if (!menuItem) return [];
    return recipes.filter((recipe) => recipe.menuItemId === menuItem.id);
  }, [recipes, menuItem]);

  const processed = useMemo(() => {
    const map = new Map<string, IProcessedMaterial>();
    processedMaterials.forEach((item: IProcessedMaterial) => map.set(item.id, item));
    return map;
  }, [processedMaterials]);

  const raws = useMemo(() => {
    const map = new Map<string, IRawMaterial>();
    rawMaterials.forEach((item: IRawMaterial) => map.set(item.id, item));
    return map;
  }, [rawMaterials]);

  const cost = recipeEntries.reduce((total, recipe) => {
    const material =
      processed.get(recipe.processedMaterialId || '') || raws.get(recipe.rawMaterialId || '');
    if (!material) return total;
    return total + Number(material.costPerUnit || 0) * Number(recipe.quantityNeeded || 0);
  }, 0);

  const price = Number(menuItem?.price || 0);
  const profit = price - cost;
  const margin = price > 0 ? (profit / price) * 100 : 0;

  if (isLoading) {
    return (
      <IonPage>
        <IonHeader translucent>
          <IonToolbar>
            <IonButtons slot="start">
              <IonBackButton defaultHref="/tabs/recipes" />
            </IonButtons>
            <IonTitle>{t('recipes')}</IonTitle>
            <LanguageToggle />
            <UserMenu />
          </IonToolbar>
        </IonHeader>
        <IonContent>
          <div style={{ display: 'flex', justifyContent: 'center', padding: '48px 0' }}>
            <IonSpinner />
          </div>
        </IonContent>
      </IonPage>
    );
  }

  if (!menuItem) {
    history.replace('/tabs/recipes');
    return null;
  }

  return (
    <IonPage>
      <IonHeader translucent>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/tabs/recipes" />
          </IonButtons>
          <IonTitle>{menuItem.product?.name || t('recipeDetails')}</IonTitle>
          <LanguageToggle />
          <UserMenu />
        </IonToolbar>
      </IonHeader>
      <IonContent fullscreen className="ion-padding">
        <section style={{ marginBottom: 24 }}>
          <h2 style={{ marginBottom: 8 }}>{t('recipeDetails')}</h2>
          <IonList>
            <IonItem>
              <IonLabel>
                <p>{t('sellingPrice')}</p>
                <h3>{formatCurrency(price, locale)}</h3>
              </IonLabel>
            </IonItem>
            <IonItem>
              <IonLabel>
                <p>{t('totalRecipeCost')}</p>
                <h3>{formatCurrency(cost, locale)}</h3>
              </IonLabel>
            </IonItem>
            <IonItem>
              <IonLabel>
                <p>{t('profit')}</p>
                <h3>{formatCurrency(profit, locale)}</h3>
              </IonLabel>
            </IonItem>
            <IonItem>
              <IonLabel>
                <p>{t('profitMargin')}</p>
                <h3>
                  {formatDecimal(margin, {
                    locale,
                    maximumFractionDigits: 1,
                    minimumFractionDigits: 1,
                  })}%
                </h3>
              </IonLabel>
            </IonItem>
          </IonList>
        </section>

        <section style={{ marginBottom: 24 }}>
          <IonText color="primary">
            <h2 style={{ marginBottom: 12 }}>{t('ingredients')}</h2>
          </IonText>
          {recipeEntries.length === 0 ? (
            <IonText color="medium">
              <p>{t('noIngredientsYet')}</p>
            </IonText>
          ) : (
            <IonList>
              {recipeEntries.map((recipe) => {
                const material =
                  processed.get(recipe.processedMaterialId || '') ||
                  raws.get(recipe.rawMaterialId || '');
                if (!material) return null;
                const baseUnit = material.unit || t('unit');
                const quantity = formatDecimal(Number(recipe.quantityNeeded || 0), {
                  locale,
                  maximumFractionDigits: 6,
                });
                return (
                  <IonItem key={recipe.id}>
                    <IonLabel>
                      <h3>{material.name || t('unknown')}</h3>
                      <p>
                        {t('quantity')}: {quantity} {baseUnit}
                      </p>
                    </IonLabel>
                  </IonItem>
                );
              })}
            </IonList>
          )}
        </section>

        <IonButton
          expand="block"
          onClick={() => history.push(`/tabs/recipes/${id}/edit`)}
        >
          {t('edit')}
        </IonButton>

      </IonContent>
    </IonPage>
  );
};

export default RecipeDetail;
